import { splitPartKey, type DraftPart } from "@/lib/assetContent";

/**
 * The typed reading of a closing-conversation `steps` document, and the
 * SURGICAL edits an authoring surface makes to one (#867).
 *
 * ## WHY THIS MODULE EXISTS, AND WHY IT IS NOT A JSON TEXTAREA
 *
 * Until this ticket a `closing_conversation` reached `AssetEditor`/`PartsEditor`:
 * a free-text "Part key" input above a textarea holding the raw steps document.
 * `NamedDocumentEditor` explains at length why that could not simply be replaced
 * with a form — `StructuredPart.data` is an unconstrained `JsonValue`, so a
 * client typing the INSIDE of one would be deciding a shape no authority in
 * this repository publishes.
 *
 * #866 published it. `closing_conversation.py` is a write-time schema for
 * exactly these documents, derived from the pinned engine's own
 * `conversation_agent/prompts.format_steps` and pinned to it by a real-checkout
 * test: `{steps: [{id, instruction}]}`, ids strictly integral and unique,
 * instructions non-blank, and at least one instruction telling the agent to emit
 * the completion token. So the authority exists now, and a typed surface over it
 * is this editor rendering the service's own contract rather than guessing one.
 *
 * ## EVERY EDIT IS APPLIED IN PLACE
 *
 * `JSON.parse` preserves insertion order for string keys and in-place mutation
 * does not disturb it, so a save that changes one instruction re-emits every
 * other key in the order it was stored. That matters for two reasons here: the
 * emitted YAML has to be the document the clinician wrote (the service
 * normalizes nothing and stores it verbatim), and a version diff between two
 * immutable versions has to be readable.
 *
 * Nothing below rebuilds a document from a narrowed view. A narrowed view is
 * precisely a list of the fields that would be dropped.
 *
 * ## WHY THE SCHEMA IS NOT RESTATED HERE
 *
 * `services/editor-service/src/editor_service/closing_conversation.py` is the
 * authority: it refuses a duplicate id, a non-integral id, a blank instruction,
 * an unsupported field and a document with no completion instruction, each with
 * a field pointer. A second copy of those rules in the browser would be free to
 * drift from the one that actually refuses — the class of duplication
 * `lib/assetContent.ts` documents at length and #300 spent a ticket deleting.
 *
 * What IS here is the projection a surface needs to RENDER, plus the two facts
 * an author cannot act without: which steps exist in stored order, and whether
 * the document as currently drafted names the completion token. A buffer that
 * cannot be read as a steps document answers `null`, and the surface says so
 * rather than presenting an empty list over content it did not understand.
 */

/** The part role holding the ordered steps, matching `placement.CLOSING_STEPS_ROLE`. */
export const CLOSING_STEPS_ROLE = "steps";

/** The part role holding the closing prompt, matching `placement.CLOSING_PROMPT_ROLE`. */
export const CLOSING_PROMPT_ROLE = "prompt";

/**
 * The pinned engine's protocol token for "this agent is finished".
 *
 * A MIRROR of `closing_conversation.ENGINE_KEYWORD_DONE`, which is itself a
 * mirror of the engine's own `capabilities.ENGINE_KEYWORD_DONE` pinned by a
 * real-checkout test. Two things make a browser copy legitimate rather than the
 * second roster this repository keeps deleting:
 *
 *   * it is NOT a rule. The service decides whether a document may be published;
 *     this literal only lets the surface SAY which token satisfies that rule, and
 *     seed a created document with one. A drifted copy would misword a hint, not
 *     admit content the runtime cannot run.
 *   * it cannot drift silently. `src/invariants/closingCompletionToken.test.ts`
 *     reads the service module and fails when the two disagree — the same
 *     mechanism `invariants/launchConfig.test.ts` uses to pin browser constants
 *     to the service's own files.
 *
 * The alternative was leaving it out: the service's refusal deliberately does
 * not quote the token (its no-PHI rule keeps refusals to field paths and
 * server-owned vocabulary), so a clinician authoring a closing from scratch
 * would meet "must instruct the agent to emit the completion token" with no way
 * to learn what the token is. That is the dead end this constant closes.
 */
export const ENGINE_KEYWORD_DONE = "<DONE_TOKEN>";

/**
 * The language the pinned runtime reads this family's documents at, as the
 * SERVER's registry states it — or `null` when the registry has not been read.
 *
 * NEVER A LITERAL IN THIS APPLICATION. `placement.CLOSING_ANCHOR_LANGUAGE` is
 * the service's, it is published on every read of
 * `GET /v1/library/placement-registry` as this family's `runtime.anchorLanguage`,
 * and a version whose documents are written at any other key is content the
 * runtime never opens — which the service refuses as
 * `placement.content_incomplete` when the project tries to adopt it.
 *
 * `null` is therefore a REFUSAL rather than a default: a create that guessed the
 * prefix would mint a version no project can pin, and a create that omitted it
 * would mint exactly the language-neutral pair this function exists to prevent.
 */
export function closingAnchorLanguage(
  registry: { families: readonly { assetType: string; runtime: { anchorLanguage?: string | null } }[] } | undefined,
): string | null {
  return (
    registry?.families.find(
      (family) => family.assetType === "closing_conversation",
    )?.runtime.anchorLanguage ?? null
  );
}

/** One step as the surface renders it. `id` is READ-ONLY stable identity. */
export interface ClosingStepView {
  /**
   * The stable identity the runtime addresses this step by.
   *
   * `number | string` because a STORED document is not necessarily one the
   * current schema would accept: versions are immutable and re-validated on
   * read, and a surface that could not render a document the service stored
   * would be a surface that cannot show a clinician what they have. The service
   * refuses a non-integral id at WRITE time; this renders whatever is there.
   */
  id: number | string;
  instruction: string;
}

/** One stored `steps` document, read for display. */
export interface ClosingStepsView {
  steps: ClosingStepView[];
  /**
   * Whether some instruction names the completion token.
   *
   * A HINT, never a gate: the service decides, and this only lets the surface
   * say — before Save is pressed — that the document has no reachable end.
   */
  instructsCompletion: boolean;
}

/** One stored steps document, with the part it came from. */
export interface ClosingStepsDocument {
  /** `DraftPart.id` — identity for React and for every edit below. */
  documentId: string;
  /** The key's language prefix, or `null` for a language-neutral document. */
  language: string | null;
  view: ClosingStepsView;
}

/** One stored steps part whose buffer is not a document this editor can read. */
export interface UnreadableClosingStepsDocument {
  documentId: string;
  language: string | null;
  view: null;
}

/**
 * The parsed document, or `null` when the buffer is not one.
 *
 * `null` covers invalid JSON and a JSON value that is not an object carrying a
 * `steps` array. Anything INSIDE the array is rendered as stored — see
 * `ClosingStepView.id`.
 */
export function readClosingSteps(text: string): ClosingStepsView | null {
  const parsed = parseDocument(text);
  if (parsed === null) {
    return null;
  }
  const steps: ClosingStepView[] = [];
  for (const entry of parsed.steps) {
    steps.push({
      id: readId(entry["id"]),
      instruction: typeof entry["instruction"] === "string" ? entry["instruction"] : "",
    });
  }
  return {
    steps,
    instructsCompletion: steps.some((step) =>
      step.instruction.includes(ENGINE_KEYWORD_DONE),
    ),
  };
}

/**
 * Every stored `steps` part, in the order the parts carry, readable or not.
 *
 * ONE ENTRY PER STORED DOCUMENT, never one per role: part keys are
 * language-prefixed and the engine reads the conversation language's own
 * document (falling back to the anchor), so `he/steps` and `en/steps` drive
 * different sessions and are edited independently. That is
 * `NamedDocumentEditor`'s own rule for prompts, applied to the structured half.
 */
export function closingStepsDocuments(
  parts: readonly DraftPart[],
): (ClosingStepsDocument | UnreadableClosingStepsDocument)[] {
  const documents: (ClosingStepsDocument | UnreadableClosingStepsDocument)[] = [];
  for (const part of parts) {
    const [language, role] = splitPartKey(part.key);
    if (role !== CLOSING_STEPS_ROLE || part.kind !== "structured") {
      continue;
    }
    documents.push({ documentId: part.id, language, view: readClosingSteps(part.text) });
  }
  return documents;
}

/** The lowest positive integer no step in this document already uses. */
export function nextStepId(view: ClosingStepsView): number {
  const used = new Set(
    view.steps
      .map((step) => (typeof step.id === "number" ? step.id : Number(step.id)))
      .filter((id) => Number.isInteger(id)),
  );
  let candidate = 1;
  while (used.has(candidate)) {
    candidate += 1;
  }
  return candidate;
}

/**
 * The starter document a newly created closing conversation carries.
 *
 * ONE STEP, and it names the completion token, because the service refuses a
 * steps document that does not: `{}` — what `NewMainConcernDialog` seeds for a
 * required structured document — cannot be published for this family, so a
 * create dialog seeding it would be a Create control whose only outcome is a
 * refusal. The text says what it is, exactly as `UNWRITTEN_PROMPT_TEXT` does for
 * the prompt half, and the clinician replaces it in the step editor.
 */
export function starterStepsDocument(): string {
  return JSON.stringify(
    {
      steps: [
        {
          id: 1,
          instruction: `This step has not been written yet. Open the closing conversation and replace this text. The closing agent must be told to emit ${ENGINE_KEYWORD_DONE} once the conversation is finished, or the session can never reach its end.`,
        },
      ],
    },
    null,
    2,
  );
}

/** One step's instruction replaced; every other byte of the document untouched. */
export function setStepInstruction(
  parts: readonly DraftPart[],
  documentId: string,
  index: number,
  instruction: string,
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    const step = document.steps[index];
    if (step !== undefined) {
      step["instruction"] = instruction;
    }
  });
}

/** The step at `from` moved to `to`; nothing else about the document changes. */
export function moveStep(
  parts: readonly DraftPart[],
  documentId: string,
  from: number,
  to: number,
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    if (to < 0 || to >= document.steps.length) {
      return;
    }
    const [moved] = document.steps.splice(from, 1);
    if (moved !== undefined) {
      document.steps.splice(to, 0, moved);
    }
  });
}

/** One step removed. The remaining steps keep their own ids, never renumbered. */
export function removeStep(
  parts: readonly DraftPart[],
  documentId: string,
  index: number,
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    document.steps.splice(index, 1);
  });
}

/** A new step appended, carrying the id the caller allocated. */
export function addStep(
  parts: readonly DraftPart[],
  documentId: string,
  id: number,
  instruction: string,
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    document.steps.push({ id, instruction });
  });
}

/**
 * The document mutated IN PLACE and re-serialised, or the parts unchanged.
 *
 * Unchanged rather than replaced-with-a-guess when the buffer is not a document
 * this module can read: the surface renders an unreadable document as
 * unreadable and offers no controls over it, so reaching here with one means a
 * caller bug, not a clinician action to interpret.
 */
function editDocument(
  parts: readonly DraftPart[],
  documentId: string,
  mutate: (document: MutableDocument) => void,
): DraftPart[] {
  return parts.map((part) => {
    if (part.id !== documentId) {
      return part;
    }
    const document = parseDocument(part.text);
    if (document === null) {
      return part;
    }
    mutate(document);
    return { ...part, text: JSON.stringify(document.root, null, 2) };
  });
}

interface MutableDocument {
  /** The whole parsed document, including any key this surface does not show. */
  root: Record<string, unknown>;
  /** The `steps` array inside it, by reference. */
  steps: Record<string, unknown>[];
}

function parseDocument(text: string): MutableDocument | null {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const root = value as Record<string, unknown>;
  const steps = root["steps"];
  if (!Array.isArray(steps)) {
    return null;
  }
  for (const entry of steps) {
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) {
      return null;
    }
  }
  // THE ARRAY ITSELF, not a copy of it: `moveStep`, `removeStep` and `addStep`
  // splice through this reference and the caller re-serialises `root`, so a
  // copied array would re-emit the document exactly as it was found.
  return { root, steps: steps as Record<string, unknown>[] };
}

/** A stored id rendered as stored: a number stays a number, anything else its JSON. */
function readId(value: unknown): number | string {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    return value;
  }
  return JSON.stringify(value) ?? "null";
}
